import classNames from "classnames";
import { AdobeEventActions } from "../../enum/adobe/adobeEventActions";
import { AdobeEventType } from "../../enum/adobe/adobeEventType";
import { StepNames } from "../../enum/adobe/stepNames";
import { StepNumbers } from "../../enum/adobe/stepNumbers";
import { SubStepNames } from "../../enum/adobe/subStepNames";
import { IconModelEnum } from "../../enum/Button/iconButton.model";
import { VarianButtonEnum } from "../../enum/Button/variantButton.model";
import { ContactDealerFormSettings, ContactDealerSettings, PrivacyPolicySettings } from "../../enum/enums";
import DataContainer from "../../services/DataContainer";
import { Button } from "../Button/Button";
import CaptchaForForms from "../CaptchaForForms/CaptchaForForms";
import { CustomCheckbox } from "../CustomCheckbox/CustomCheckbox";
import { ContactFormThankYouModal } from "../Modal/ContactFormThankYouModal";
import { Typography } from "../typography";
import { SalesforceInput } from "../Input/SalesforceInput";
import { ChangeEvent, LegacyRef } from "react";
import { CurrentLocaleModel } from "../../context/DataContainerContext/types";
import { DataType } from "../../pages/Summary/formType";

interface SalesforceFormProps {
  resetRef: LegacyRef<HTMLFormElement> | undefined;
  renderControlKey: number;
  formData: DataType;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  isEmailValid: boolean;
  onCheckboxChange: (e: ChangeEvent<HTMLInputElement>, name: string) => void;
  marketingCheckboxErrorIndicator: boolean;
  requiredInput: boolean;
  requiredFields: boolean;
  captchaRequired: boolean;
  resetForm: boolean;
  contactFormModalOpen: boolean;
  setContactFormModalOpen: (value: boolean) => void;
  salesforceStatus: string;
  salesforceMessage: string;
  submitForm: (data: DataType) => void;
  setCaptchaRequired: (value: boolean) => void;
  showCTA: (sectionName: string, key: string) => boolean;
  correctCaptcha: boolean;
  setCorrectCaptcha: (value: boolean) => void;
  currentLocale: CurrentLocaleModel | undefined;
  locale: string | undefined;
  market: string | undefined;
  isSubmitting?: boolean;
}

export const SalesforceForm = ({
  resetRef, 
  renderControlKey, 
  formData, 
  onChange, 
  isEmailValid, 
  onCheckboxChange, 
  marketingCheckboxErrorIndicator, 
  requiredInput,
  requiredFields,
  captchaRequired,
  resetForm,
  contactFormModalOpen,
  setContactFormModalOpen,
  salesforceStatus,
  salesforceMessage,
  submitForm,
  setCaptchaRequired,
  showCTA,
  correctCaptcha,
  setCorrectCaptcha,
  currentLocale,
  locale,
  market,
  isSubmitting = false,
}: SalesforceFormProps) => {
  return (
    <form className="flex flex-col gap-4" ref={resetRef} key={renderControlKey}>
      <div className="flex flex-col gap-5">
        <div className="flex flex-col md:flex-row justify-between gap-5 md:gap-8">
          <SalesforceInput
            variant="primary"
            type="text"
            name="name"
            label={DataContainer.sectionKey(
              ContactDealerFormSettings.CONTACT_DEALER_FORM,
              ContactDealerFormSettings.CONTACT_DEALER_FIRSTNAME_TEXT
            )}
            value={formData.name}
            onChangeEvent={onChange}
            onClickEvent={() => DataContainer.sendDataToAdobeDataLayer(AdobeEventType.APPLICATION_INTERACTION, StepNumbers.STEP_8, StepNames.SUMMARY, SubStepNames.SUMMARY, AdobeEventActions.FORM_START, {}, "")}
            isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_NAME_REQUIRED) === "true" && requiredInput}
          />

          <SalesforceInput
            variant="primary"
            type="text"
            name="lastname"
            label={DataContainer.sectionKey(
              ContactDealerFormSettings.CONTACT_DEALER_FORM,
              ContactDealerFormSettings.CONTACT_DEALER_LASTNAME_TEXT
            )}
            value={formData.lastname}
            onChangeEvent={onChange}
            isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_LASTNAME_REQUIRED) === "true" && requiredInput}
          />
        </div>

        <div className="flex flex-col md:flex-row justify-between gap-5 md:gap-8">
          <SalesforceInput
            variant="primary"
            type="email"
            name="email"
            label={DataContainer.sectionKey(
              ContactDealerFormSettings.CONTACT_DEALER_FORM,
              ContactDealerFormSettings.CONTACT_DEALER_EMAIL_TEXT
            )}
            value={formData.email}
            onChangeEvent={onChange}
            isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_EMAIL_REQUIRED) === "true" && requiredInput}
          />
          {(!isEmailValid && formData.email !== "") &&
            <span className="text-red text-xl h-4">{DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_EMAIL_INVALID_TEXT)}</span>
          }

          <SalesforceInput
            variant="primary"
            type="text"
            name="zip"
            label={DataContainer.sectionKey(
              ContactDealerFormSettings.CONTACT_DEALER_FORM,
              ContactDealerFormSettings.CONTACT_DEALER_ZIP_TEXT
            )}
            value={formData.zip}
            onChangeEvent={onChange}
            isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_ZIP_REQUIRED) === "true" && requiredInput}
          />
        </div>
      
        <div className="flex flex-col md:flex-row justify-between gap-5 md:gap-8">
          <SalesforceInput
            variant="primary"
            type="text"
            name="city"
            label={DataContainer.sectionKey(
              ContactDealerFormSettings.CONTACT_DEALER_FORM,
              ContactDealerFormSettings.CONTACT_DEALER_CITY_TEXT
            )}
            value={formData.city}
            onChangeEvent={onChange}
            isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_CITY_REQUIRED) === "true" && requiredInput}
          />

          <SalesforceInput
            variant="primary"
            type="text"
            name="company"
            label={DataContainer.sectionKey(
              ContactDealerFormSettings.CONTACT_DEALER_FORM,
              ContactDealerFormSettings.CONTACT_DEALER_COMPANY_TEXT
            )}
            value={formData.company}
            onChangeEvent={onChange}
            isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_COMPANY_REQUIRED) === "true" && requiredInput}
          />
        </div>
  
        <SalesforceInput
          variant="primary"
          type="textarea"
          name="message"
          label={DataContainer.sectionKey(
            ContactDealerFormSettings.CONTACT_DEALER_FORM,
            ContactDealerFormSettings.CONTACT_DEALER_MESSAGE_TEXT
          )}
          value={formData.message}
          onChangeEvent={onChange}
          isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_MESSAGE_REQUIRED) === "true" && requiredInput}
        />
      </div>

      <div className="flex flex-col gap-7">
        <div className="flex flex-col gap-7">
          <Typography tag="p" className="text-2xs">
            {DataContainer.sectionKey(
              ContactDealerFormSettings.CONTACT_DEALER_FORM,
              ContactDealerFormSettings.CONTACT_DEALER_REQUIRED_TIP_TEXT
            )}
          </Typography>
          <div className="flex flex-col gap-7">
            <Typography tag="p" className="text-2xl text-light-grey break-words">
              {DataContainer.sectionKey(
                PrivacyPolicySettings.PRIVACY_POLICY,
                PrivacyPolicySettings.PRIVACY_POLICY_TEXT
              )}<span><a href={`https://www.volvogroup.com/en/tools/privacy/privacy-${(currentLocale?.locale === "nl-be" || currentLocale?.locale === "fr-be") ? locale : market}.html`} target="_blank" rel="noreferrer" className="underline underline-offset-1 text-2xl text-blue-700 mb-4" >{`(https://www.volvogroup.com/en/tools/privacy/privacy-${market}.html)`}</a></span>
            </Typography>

            <div className="flex flex-col">
              <div className='relative'>
                <CustomCheckbox
                  name="marketingCheckbox"
                  label={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_OPTIN_MARKETING_CHECKBOX_TEXT)}
                  onChangeEvent={(e) => onCheckboxChange(e, "marketingCheckbox")}
                  customClassNames={classNames({ "border-red rounded": marketingCheckboxErrorIndicator && requiredInput && (DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_OPTIN_MARKETING_CHECKBOX_REQUIRED) === "true") })}
                  customLabelClasses={classNames({ "text-red": marketingCheckboxErrorIndicator && requiredInput && (DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_OPTIN_MARKETING_CHECKBOX_REQUIRED) === "true")})}
                />
              </div>
              {(marketingCheckboxErrorIndicator && requiredInput && showCTA(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_OPTIN_MARKETING_CHECKBOX_REQUIRED)) && <span className="text-red pt-2 text-xl h-4">{DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_OPTIN_MARKETING_CHECKBOX_REQUIRED_TEXT)}</span>}
            </div>
          </div>
          <CaptchaForForms salesforce={true} required={!(captchaRequired || requiredFields)} captcha={(e) => setCaptchaRequired(e)} resetCaptcha={resetForm} setCorrectCaptcha={(e) => setCorrectCaptcha(e)} correctCaptcha={correctCaptcha} placeholder={ContactDealerSettings.CAPTCHA_LABEL}/>
        </div>

        <Button
          variant={VarianButtonEnum.PRIMARY}
          text={DataContainer.sectionKey(
            ContactDealerFormSettings.CONTACT_DEALER_FORM,
            ContactDealerFormSettings.CONTACT_DEALER_SEND_TEXT
          )}
          icon={isSubmitting ? IconModelEnum.LOADING : IconModelEnum.SUBMIT}
          customClassNames="bg-blue-light"
          disabled={isSubmitting}
          onClickEvent={() => {
            submitForm(formData);
          }}
        />
      </div>
      {(contactFormModalOpen) ? (
        <ContactFormThankYouModal
          setContactFormModalOpen={setContactFormModalOpen}
          salesforceStatus={salesforceStatus}
          salesforceMessage={salesforceMessage}
        />

      ) : null}
    </form>
  );
};